import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Tooltip } from 'components/common';
import { FormGroup, RadioButtonCard, RadioGroup } from 'components/common/form';
import { Icon } from 'components/common/icons';
import {
  SettingsPicturePosition,
  SettingsPictureScreen
} from 'components/courses/settings';

interface Props {
  bannerImageValue: string;
  bannerLayoutField: string;
  bannerLayoutValue: string;
  bannerPositionField: string;
  bannerPositionValue: string;
  id: string;
}

const HomePageSettingsBannerPreview = ({
  bannerImageValue,
  bannerLayoutField,
  bannerLayoutValue,
  bannerPositionField,
  bannerPositionValue,
  id
}: Props) => {
  const { messages, theme } = useReduxState();

  const pictureRadius = Math.round(
    Number(theme.cardBorderRadius.slice(0, -2)) / 2 || 10
  );

  return (
    <div className="banner-preview">
      <div className={`preview preview-${bannerLayoutValue}`}>
        <SettingsPictureScreen
          contentHeight={200}
          headerHeight={20}
          subHeaderHeight={20}
          title={messages.homePage}
        >
          <div className="preview-inner">
            <div
              className="preview-image"
              style={{ backgroundImage: `url(${bannerImageValue})` }}
            />
          </div>
        </SettingsPictureScreen>
      </div>

      <div className="options">
        <div className="layout">
          <FormGroup label={messages.commonLayout}>
            <RadioGroup
              id={`home-page-banner-image-layout-${id}`}
              initialValue={bannerLayoutValue}
              field={bannerLayoutField}
            >
              <div className="layout-options">
                {[
                  {
                    description:
                      messages.homePageSettingsBannerFullWidthDescription,
                    label: messages.homePageSettingsBannerFullWidth,
                    value: 'full-width'
                  },
                  {
                    description:
                      messages.homePageSettingsBannerFixedSizeDescription,
                    label: messages.homePageSettingsBannerFixedSize,
                    value: 'fixed-size'
                  }
                ].map((option) => (
                  <RadioButtonCard
                    id={`home-page-banner-image-layout-${option.value}-${id}`}
                    field={bannerLayoutField}
                    key={option.value}
                    value={option.value}
                  >
                    <div className="layout-option">
                      <div className="layout-option-title">{option.label}</div>

                      <Tooltip content={option.description}>
                        <div className="layout-option-icon">
                          <Icon icon="question-circle" />
                        </div>
                      </Tooltip>
                    </div>
                  </RadioButtonCard>
                ))}
              </div>
            </RadioGroup>
          </FormGroup>
        </div>

        <div className="image-position">
          <FormGroup label={messages.settingsCoursePicturePosition}>
            <SettingsPicturePosition
              field={bannerPositionField}
              id={`home-page-banner-image-position-${id}`}
              initialValue={bannerPositionValue}
              messages={messages}
            />
          </FormGroup>
        </div>
      </div>

      <style jsx>{`
        .banner-preview {
          display: flex;
          flex-direction: column;
          gap: 15px 30px;
        }
        .preview {
          flex-grow: 1;
          max-width: 540px;
        }
        .preview-image {
          background-size: cover;
          background-position: ${bannerPositionValue};
          width: 100%;
        }
        .preview-full-width .preview-image {
          height: 80px;
        }
        .preview-fixed-size .preview-inner {
          padding: 15px 30px;
        }
        .preview-fixed-size .preview-image {
          border-radius: ${pictureRadius}px;
          padding-top: 20%;
        }
        .layout-options {
          display: flex;
          gap: 15px;
        }
        .layout-option {
          align-items: center;
          display: flex;
          gap: 7px;
          margin-right: 10px;
        }
        .layout-option-icon {
          font-size: 1.4rem;
        }
        .image-position {
          margin-top: 10px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .banner-preview {
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsBannerPreview;
